import { ArrowRight, Phone } from "lucide-react";
import { BRAND, HERO_IMAGE } from "@/data/content";

export default function Hero({ onBook }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden hex-bg-strong"
    >
      {/* Hero background (athletic figures + brand banner) */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="NextGen Performance"
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ng-bg via-ng-bg/85 to-ng-bg/65" />
        <div className="absolute inset-0 bg-gradient-to-b from-ng-bg/60 via-transparent to-ng-bg" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-44 pb-24 min-h-screen flex flex-col justify-center">
        <div className="max-w-4xl">
          <div className="fade-up fade-up-delay-1 flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-ng-blue electric-pulse" />
            <span className="label-eyebrow">{BRAND.promise}</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="fade-up fade-up-delay-2 font-display italic font-black text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] tracking-tight leading-[0.9]"
          >
            <span className="chrome-text">{BRAND.short_split.silver}</span>
            <span className="electric-text">{BRAND.short_split.blue}</span>
          </h1>

          <h2 className="fade-up fade-up-delay-2 font-display font-bold uppercase tracking-[0.35em] text-ng-blue text-lg md:text-2xl mt-3 ml-1">
            {BRAND.sub}
          </h2>

          <p
            data-testid="hero-subheadline"
            className="fade-up fade-up-delay-3 mt-8 text-2xl md:text-3xl text-white font-display font-semibold tracking-wide"
          >
            Coaching. Nutrition. <span className="text-ng-blue">Results.</span>
          </p>
          <p className="fade-up fade-up-delay-3 mt-4 text-lg text-white/70 font-light max-w-2xl leading-relaxed">
            Tailored programs built around your goals, lifestyle and performance &mdash; backed by research-grade peptide options for clients who want to optimize beyond the gym.
          </p>

          <div className="fade-up fade-up-delay-4 mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-apply"
              onClick={onBook}
              className="group inline-flex items-center justify-center gap-3 bg-ng-blue text-white px-8 py-4 uppercase tracking-[0.22em] text-sm font-bold font-display hover:bg-ng-blueLight transition-all shadow-[0_0_40px_-6px_rgba(30,144,255,0.7)]"
            >
              Start Your Transformation
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <a
              href={`tel:${BRAND.phone_link}`}
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center gap-3 border border-ng-line text-white px-8 py-4 uppercase tracking-[0.22em] text-sm font-bold font-display hover:border-ng-blue hover:text-ng-blue transition-colors"
            >
              <Phone size={14} strokeWidth={1.5} />
              Text or Call Coach J · {BRAND.phone}
            </a>
          </div>

          {/* Stats strip */}
          <div className="fade-up fade-up-delay-4 mt-16 grid grid-cols-3 gap-6 md:gap-12 max-w-2xl border-t border-ng-line pt-8">
            <Stat value="1,400+" label="Clients Coached" />
            <Stat value="Medford" label={BRAND.region.replace("Serving ", "")} />
            <Stat value="100%" label="Tailored Plans" />
          </div>
        </div>
      </div>

      {/* Bottom scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-3">
        <span className="label-muted">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-ng-blue to-transparent" />
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div data-testid={`hero-stat-${label.split(" ")[0].toLowerCase()}`}>
      <div className="font-display font-bold text-3xl md:text-4xl text-white">
        {value}
      </div>
      <div className="label-muted mt-1">{label}</div>
    </div>
  );
}
